import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FiLoader, FiTrash2, FiEdit2, FiX } from 'react-icons/fi';
import ConfirmModal from '../../common/ConfirmModal';

interface Question {
  id: number;
  text: string;
  category: {
    id: number;
    name: string;
  } | null;
  created_at: string;
}

interface ListQuestionsProps {
  onEditQuestion: (questionId: number) => void; // Handler to trigger editing in parent
}

const ListQuestions: React.FC<ListQuestionsProps> = ({ onEditQuestion }) => {
  const [questions, setQuestions] = useState<Question[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<{ id: number, name: string } | null>(null);

  // Nuovi stati per la ricerca e la paginazione // New states for search and pagination
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const itemsPerPage = 10; // Numero di elementi per pagina // Number of items per page

  const fetchQuestions = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/questions`);
      setQuestions(response.data);
    } catch (err) {
      setError("Errore nel caricamento delle domande"); // Error loading questions
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchQuestions();
  }, []);

  // Filtrare le domande in base al termine di ricerca (testo o nome della categoria) // Filter questions by search term (text or category name)
  const filteredQuestions = questions.filter((q) =>
    q.text.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (q.category?.name.toLowerCase().includes(searchTerm.toLowerCase()) ?? false)
  );

  // Ordinare le domande per data di creazione in ordine decrescente (le più recenti per prime) // Sort questions by creation date descending (newest first)
  const sortedQuestions = [...filteredQuestions].sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

  const totalPages = Math.ceil(sortedQuestions.length / itemsPerPage);
  const displayedQuestions = sortedQuestions.slice((page - 1) * itemsPerPage, page * itemsPerPage);

  // Reset della pagina se cambia il termine di ricerca // Reset page if search term changes
  useEffect(() => {
    setPage(1);
  }, [searchTerm]);

  const handleDelete = (question: Question) => {
    setDeleteConfirm({ id: question.id, name: question.text });
  };

  const confirmDelete = async () => {
    if (!deleteConfirm) return;
    try {
      await axios.delete(`${process.env.REACT_APP_API_URL}/questions/${deleteConfirm.id}`);
      setSuccessMessage("Domanda eliminata con successo"); // Question deleted successfully
      await fetchQuestions();
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: any) {
      setErrorMessage("Errore nella cancellazione della domanda"); // Error deleting question
      setTimeout(() => setErrorMessage(null), 3000);
    } finally {
      setDeleteConfirm(null);
    }
  };


  if (error) {
    return (
      <div className="text-red-500 text-center p-4">{error}</div>
    );
  }

  return (
    <>
      <div className="bg-white rounded-xl shadow-lg border border-gray-200/80">
        {/* Header della card // Card header */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center">
            <h2 className="text-xl font-semibold text-gray-800">Elenco delle domande</h2> {/* List of questions */}
            <div className="flex-1 mx-4">
              <input 
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Cerca una domanda o una categoria..." // Search for a question or category
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            <span className="px-3 py-1 bg-indigo-50 text-indigo-600 rounded-full text-sm font-medium">
              {sortedQuestions.length} domande {/* {sortedQuestions.length} questions */}
            </span>
          </div>
          {successMessage && (
            <div className="mt-2 mb-4 px-4 py-2 bg-green-100 text-green-700 text-sm rounded flex items-center justify-between">
              {successMessage}
              <button onClick={() => setSuccessMessage(null)} className="ml-2">
                <FiX className="w-4 h-4 text-green-700" />
              </button>
            </div>
          )}
          {errorMessage && (
            <div className="mt-2 mb-4 px-4 py-2 bg-red-100 text-red-700 text-sm rounded flex items-center justify-between">
              {errorMessage}
              <button onClick={() => setErrorMessage(null)} className="ml-2">
                <FiX className="w-4 h-4 text-red-700" />
              </button>
            </div>
          )}
        </div>

        {/* Corpo della card: tabella e paginazione // Card body: table and pagination */}
       
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gradient-to-r from-[rgb(149,71,254)] via-[rgb(89,96,211)] to-[rgb(51,112,183)]">
                <tr className="border-b border-[rgb(51,112,183)]">
                  <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">ID</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Domanda</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Categoria</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Data di creazione</th> 
                  <th className="px-6 py-3 text-right text-xs font-medium text-white uppercase tracking-wider">Azioni</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {displayedQuestions.map((question, index) => (
                  <tr key={question.id} className="odd:bg-white even:bg-gray-50 hover:bg-gray-100 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{(page - 1) * itemsPerPage + index + 1}</td>
                    <td className="px-6 py-4 text-sm text-gray-900">{question.text}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {question.category?.name || "Senza categoria"} {/* Without category */}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(question.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => onEditQuestion(question.id)} // Trigger edit via parent
                          className="p-1 rounded-full hover:bg-indigo-50 text-indigo-600 transition-colors"
                          title="Modifica" /* Edit */
                        >
                          <FiEdit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(question)}
                          className="p-1 rounded-full hover:bg-red-50 text-red-600 transition-colors"
                          title="Elimina" /* Delete */
                        >
                          <FiTrash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {filteredQuestions.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-6 py-8 text-center">
                      <div className="flex flex-col items-center gap-2">
                        <p className="text-gray-500 text-sm">Nessuna domanda trovata</p> {/* No questions found */}
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
         

        <div className="px-4 py-4 flex justify-between items-center">
        <button
          onClick={() => setPage(page > 1 ? page - 1 : 1)}
          disabled={page === 1}
          className="px-4 py-2 text-sm text-gray-700 bg-gray-200 rounded-lg"
        >
          Precedente
        </button>
        <span className="text-sm text-gray-500">Pagina {page} di {totalPages}</span>
        <button
          onClick={() => setPage(page < totalPages ? page + 1 : totalPages)}
          disabled={page === totalPages}
          className="px-4 py-2 text-sm text-gray-700 bg-gray-200 rounded-lg"
        >
          Successivo
        </button>
      </div>
        </div>
      </div>

      <ConfirmModal
        isOpen={deleteConfirm !== null}
        title="Conferma la cancellazione"  
        message="Sei sicuro di voler eliminare questa domanda?" /* Are you sure you want to delete this question? */
        onConfirm={confirmDelete}
        onCancel={() => setDeleteConfirm(null)}
      />
    </>
  );
};

export default ListQuestions;
